// VfxLayer — mounts active VfxInstances and feeds them new triggers
// derived from incoming game events. Mirrors the useSounds pattern:
// track lastCount so we slice just the newly-arrived events each render,
// and clamp to 0 when the events array resets (new match).
//
// Sounds listed in the VFX trigger are played through the shared
// SoundManager so audio + visuals stay in sync.

import type { GameEvent } from '@aod/protocol';
import { useEffect, useRef, useState } from 'react';
import { getSoundManager } from '../audio/useSounds.js';
import { useChampionUi } from '../champion/championStore.js';
import { VfxInstance } from './VfxInstance.js';
import { Projectile } from './vfx/Projectile.js';
import { eventToVfx } from './vfx/eventToVfx.js';
import { getVfxDef } from './vfx/registry.js';

interface ActiveVfx {
  readonly id: number;
  readonly defId: string;
  readonly position: readonly [number, number, number];
  readonly beamTargetOverride?: readonly [number, number, number];
  readonly projectileTarget?: readonly [number, number, number];
}

let nextVfxId = 0;

interface VfxLayerProps {
  readonly events: ReadonlyArray<GameEvent | { type: string; payload?: Record<string, unknown> }>;
}

export function VfxLayer({ events }: VfxLayerProps) {
  const [active, setActive] = useState<ReadonlyArray<ActiveVfx>>([]);
  const lastCountRef = useRef(0);

  useEffect(() => {
    // Reset on new match (events array shrinks back to 0).
    if (events.length < lastCountRef.current) {
      lastCountRef.current = 0;
    }
    const startIdx = lastCountRef.current;
    if (events.length <= startIdx) return;
    const newEvents = events.slice(startIdx);
    lastCountRef.current = events.length;

    const triggered: ActiveVfx[] = [];
    const sound = getSoundManager();
    for (const e of newEvents) {
      const t = eventToVfx(e);
      if (!t) continue;
      if (!getVfxDef(t.vfxId)) continue;
      // Projectiles without a target would fall through to VfxInstance and
      // render a zero-length beam at the origin — skip them instead.
      if (t.vfxId === 'projectile:arrow' && !t.projectileTarget) continue;
      triggered.push({
        id: nextVfxId++,
        defId: t.vfxId,
        position: t.position,
        beamTargetOverride: t.beamTargetOverride,
        projectileTarget: t.projectileTarget,
      });
      if (t.soundId) sound.play(t.soundId, t.position);
    }

    if (triggered.length > 0) {
      setActive((prev) => [...prev, ...triggered]);
    }
  }, [events]);

  // Firestorm casts queued by champion/castAbility.ts: breath at the dragon's
  // mouth (beam to the target) + blast at the target hex.
  const firestorms = useChampionUi((s) => s.firestorms);
  useEffect(() => {
    if (firestorms.length === 0) return;
    const casts = useChampionUi.getState().drainFirestorms();
    if (casts.length === 0) return;
    const triggered: ActiveVfx[] = [];
    for (const c of casts) {
      triggered.push({
        id: nextVfxId++,
        defId: 'legend:firestorm',
        position: c.from,
        beamTargetOverride: c.to,
      });
      triggered.push({ id: nextVfxId++, defId: 'legend:firestorm_blast', position: c.to });
    }
    setActive((prev) => [...prev, ...triggered]);
  }, [firestorms]);

  // Withering / Bulwark / Ascend effects queued by the same cast path.
  const effects = useChampionUi((s) => s.effects);
  useEffect(() => {
    if (effects.length === 0) return;
    const queued = useChampionUi.getState().drainEffects();
    if (queued.length === 0) return;
    setActive((prev) => [
      ...prev,
      ...queued.map((e) => ({ id: nextVfxId++, defId: e.defId, position: e.position })),
    ]);
  }, [effects]);

  return (
    <>
      {active.map((v) => {
        const def = getVfxDef(v.defId);
        if (!def) return null;
        // Projectiles render via a dedicated primitive instead of the
        // standard VFX composition (Beam/Particles/Ring/Shake).
        if (v.defId === 'projectile:arrow' && v.projectileTarget) {
          return (
            <Projectile
              key={`proj-${v.id}`}
              origin={v.position}
              target={v.projectileTarget}
              durationSec={def.durationSec}
              color={def.beam?.color ?? '#ffcc66'}
              onComplete={() => setActive((cur) => cur.filter((x) => x.id !== v.id))}
            />
          );
        }
        return (
          <VfxInstance
            key={`vfx-${v.id}`}
            def={def}
            position={v.position}
            beamTargetOverride={v.beamTargetOverride}
            onComplete={() => setActive((cur) => cur.filter((x) => x.id !== v.id))}
          />
        );
      })}
    </>
  );
}
